import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { UploadedPhoto } from '../../prop-types';
import AdvancedEditorController from '../../common/advanced-editor/controller';
import {
  getViewport,
  WithViewportSize,
} from '../../../_utils/hoc/with-viewport-size';
import ImageCropper from '../../common/advanced-editor/image-cropper';
import { SliderControlsPanel } from '../../desktop/advanced-editor/slider-controls-panel';
import { SliderAndButtonsPanel } from '../../mobile/advanced-editor/slider-and-buttons-panel';
import { MODES } from './constants';
import Header from './header';
import ImageEditPreview from '../../mobile/advanced-editor/image-edit-preview';
import OrientationMessage from '../../../_utils/orientation-message';
import screenAndStyle from '../../../_utils/screen-and-style';
import AdvancedEditorWarningOverlay from '../../common/photo-quality/advanced-editor-warning-overlay';
import Button from '../../../_common/button';

export const AdvancedEditor = React.forwardRef(
  (
    {
      imageSrc,
      curCrop,
      photo,
      sizeArea,
      editMode,
      effectUrls,
      initialZoom,
      refs,
      handlers,
      open,
      lowDpiWarningType,
      ready,
      loadImageError,
    },
    ref,
  ) => {
    const { CROP, ROTATE } = MODES;
    const showCropper = [CROP, ROTATE].includes(editMode);
    const orientationMessage =
      'Please rotate your device to continue editing your print.';

    const { height } = getViewport();
    const isMobile = screenAndStyle.isBootstrapLGDown();

    const cropperDimensions = {
      height: isMobile ? height - 285 : '100%',
      width: '100%',
    };

    const controlsDisabled = !ready;

    return (
      <div
        ref={ref}
        className="advanced-editor__wrapper d-flex flex-column"
        role="dialog"
        aria-label="Edit photo"
        aria-describedby="advanced-editor-header"
        tabIndex="-1">
        <Header
          id={'advanced-editor-header'}
          className="advanced-editor__header d-flex d-lg-none justify-content-center align-items-center grey-zone "
          title={editMode}
        />
        <div
          className={classNames('advanced-editor__body row', {
            preview: !showCropper,
          })}>
          {loadImageError ? (
            <div className="advanced-editor__error col-12 d-flex flex-column justify-content-center align-items-center h-100 text-center">
              <p className="mb-3">{loadImageError}</p>
              <Button
                type="button"
                className="btn btn-secondary"
                onClick={handlers.close}>
                Close
              </Button>
            </div>
          ) : (
            <>
              <div
                style={{
                  maxHeight: cropperDimensions.height,
                  minHeight: cropperDimensions.height,
                }}
                className="advanced-editor__photo col-xl-9 col-lg-8 col-sm-12 p-0">
                {showCropper ? (
                  <>
                    <ImageCropper
                      cropperRef={refs.cropperRef}
                      style={cropperDimensions}
                      imageSrc={imageSrc}
                      curCrop={curCrop}
                      photo={photo}
                      sizeArea={sizeArea}
                      {...handlers}
                    />
                    <AdvancedEditorWarningOverlay
                      warningType={lowDpiWarningType}
                    />
                  </>
                ) : (
                  <ImageEditPreview photo={photo} sizeArea={sizeArea} style={cropperDimensions} />
                )}
              </div>
              {!isMobile && (
                <div className="col-xl-3 col-lg-4 grey-zone">
                  <SliderControlsPanel
                    photo={photo}
                    sizeArea={sizeArea}
                    initialZoom={initialZoom}
                    effectUrls={effectUrls}
                    disabled={controlsDisabled}
                    {...refs}
                    {...handlers}
                  />
                </div>
              )}
            </>
          )}
        </div>
        {isMobile && open && !loadImageError && (
          <SliderAndButtonsPanel
            editMode={editMode}
            imageSrc={imageSrc}
            curCrop={curCrop}
            photo={photo}
            sizeArea={sizeArea}
            initialZoom={initialZoom}
            effectUrls={effectUrls}
            disabled={controlsDisabled}
            {...refs}
            {...handlers}
          />
        )}
        <OrientationMessage message={orientationMessage} />
      </div>
    );
  },
);

AdvancedEditor.displayName = 'AdvancedEditor';

AdvancedEditor.propTypes = {
  imageSrc: PropTypes.string,
  photo: UploadedPhoto,
  editMode: PropTypes.oneOf(Object.values(MODES)).isRequired,
  curCrop: PropTypes.shape({
    width: PropTypes.oneOfType([PropTypes.number]),
    height: PropTypes.oneOfType([PropTypes.number]),
  }),
  initialZoom: PropTypes.number.isRequired,
  refs: PropTypes.shape({
    [PropTypes.string]: PropTypes.func,
  }),
  handlers: PropTypes.shape({
    [PropTypes.string]: PropTypes.func,
  }),
  effectUrls: PropTypes.shape({
    [PropTypes.string]: PropTypes.string,
  }),
  open: PropTypes.bool,
  lowDpiWarningType: PropTypes.oneOf(['original', 'crop']),
  ready: PropTypes.bool,
  loadImageError: PropTypes.string,
  sizeArea: PropTypes.arrayOf(PropTypes.number),
};

AdvancedEditor.defaultProps = {
  imageSrc: null,
  photo: null,
  curCrop: null,
  effectUrls: null,
  refs: null,
  handlers: null,
  open: false,
  lowDpiWarningType: null,
  ready: false,
  loadImageError: null,
};

export default WithViewportSize(AdvancedEditorController(AdvancedEditor));
